<script lang="ts">
  import { type RssFeedContent } from "$lib/elev8Api/contentTypes";
  import defaultLogoUrl from "$assets/elev8-by-vertical-city-logo-emblem.svg";
  import { t } from "$lib/utils/i18n";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";

  interface Props {
    content: RssFeedContent;
  }

  let { content }: Props = $props();
  const config = getConfigurationContext();

  const isSportsnet = $derived(content.provider === "sportsnet");
  const isCityNews = $derived(content.provider === "citynews");
  // Multi-MB animated GIF feed images wedge WPE renderers fleet-wide (#304);
  // f_auto preserves animation, so skip any image whose source is a .gif.
  const isGifSource = (url: string) => /\.gif($|[?#]|%3f|%23)/i.test(url);
  const hasImage = $derived(
    !!content.asset_url && !isGifSource(content.asset_url)
  );
  const hasTitle = $derived(!!content.title_text);
  const hasDescription = $derived(!!content.body_text);
  const logoUrl = $derived(content.signature_url || defaultLogoUrl); // should this have no fallback and conditionally render?

  //HACK: CRITICAL. for conditional classes, you must use :global()
  // (if you still want it scoped, do a conditional wrapper class and nest :global class(s)) - e.g. .conditional-local-class :global(.nested-specific-classes) { ... }
  // Svelte (basically all frameworks now) uses :where() pseudo class to scope styles with 0 specificity.
  // :where() cannot be polyfilled without changing specificity and postcss-preset-env just drops it silently. There are no plugins that I can find
</script>

<div
  class={[
    "vc-content-layout",
    isSportsnet && "sportsnet",
    isCityNews && "citynews",
  ]}
  id="rss-feed"
>
  {#if hasImage}
    <img
      src={content.asset_url}
      alt={content.title_text}
      class="vc-content-background-image background-image"
      crossorigin="anonymous"
    />
  {/if}
  <div class="logo-container">
    {#if !isSportsnet && !isCityNews}
      <img src={logoUrl} alt={t("alt.logo", config.language)} class="logo-top" crossorigin="anonymous" />
    {/if}
  </div>
  <div
    class={[
      "text-container",
      hasDescription && "text-container-with-description",
    ]}
  >
    {#if hasTitle}
      <div class="title-container">
        {#if !hasDescription}
          {#if isSportsnet || isCityNews}
            <div class="title-logo-container">
              <img
                src={logoUrl}
                alt={t("alt.logo", config.language)}
                class="title-logo"
                crossorigin="anonymous"
              />
            </div>
          {:else}
            <div class="title-decoration"></div>
          {/if}
        {/if}
        <div class="title-wrap-for-line-clamp">
          <h2 class="title">
            {content.title_text}
          </h2>
        </div>
      </div>
    {/if}
    {#if hasDescription}
      <div class="description-wrap-for-line-clamp">
        <p class="description">{content.body_text}</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .background-image {
    height: auto;
    aspect-ratio: 16/9;
    top: auto;
    left: auto;
  }
  @supports not (aspect-ratio: 16/9) {
    /* fall back to parent .vc-content-background-image's full-fill */
    .background-image {
      height: 100%;
      top: 0;
      left: 0;
    }
  }

  .logo-container {
    flex: 1 1 auto;
    display: flex;
    flex-flow: row-reverse;
    z-index: 10;
    margin-top: 5px;
    margin-right: 5px;
    animation: slide-in-from-left 1s ease-in-out;
  }
  .logo-top {
    height: 6vh;
    width: auto;
  }

  .text-container {
    flex: 0 0 auto;
    align-self: center;
    z-index: 10;
    position: relative;
    margin: 2em 3em;
    border: 1px solid var(--vc-neutral-light-20);
    padding: 0;
    animation: slide-in-from-left-text-content 1s ease-in-out;
    color: var(--vc-primary);
    text-align: left;
    backdrop-filter: blur(1em);
  }
  .text-container-with-description {
    margin: 0;
    border: none;
    /* :global() prevents generating :where() (can't polyfill) */
    :global(.title-container) {
      width: 100%;
    }
    :global(.title-wrap-for-line-clamp) {
      padding: 0.5em 1em;
      background-color: var(--vc-primary-80);
      font-size: 0.8em;
      width: 100%;
    }
    :global(.title) {
      color: var(--vc-white);
    }
  }

  .title-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    animation: slide-in-from-right 1s ease-in-out;
    line-height: 1.3;
    background-color: var(--vc-white-50);
    font-size: calc(3.6rem * var(--layout-font-scale, 1));
  }
  .title-wrap-for-line-clamp {
    padding: 0.25em 0.6em;
    flex: 1 1 auto;
  }
  .title {
    margin: 0;
    color: var(--vc-primary);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .title-decoration {
    flex: 0 0 0.8em;
    align-self: stretch;
    background: linear-gradient(
      to right,
      var(--vc-primary) 50%,
      var(--vc-secondary) 50%
    );
  }
  .title-logo {
    width: 100%;
    object-fit: contain;
  }
  .title-logo-container {
    flex: 0 0 3em;
    max-height: 3.1em; /* won't need if aspect-ratio polyfill */
    align-self: stretch;
    aspect-ratio: 1/1;
  }

  .description-wrap-for-line-clamp {
    padding: 1.5rem 3rem;
    background-color: var(--vc-white-80);
    font-size: calc(2.75rem * var(--layout-font-scale, 1));
  }
  .description {
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    /* animation: slide-in-from-left 1s ease-in-out; */
  }
  /*
  sportsnet (scraped from website)
  white: #fff
  black: #000
  primary: #002b49
  secondary: #005eb8
  accent: #da291c
  neutral-dark: #343434
  neutral-light: #F9F9F9
  neutral: #e9ecef
  others: #1D6DAA #5b6770
  banner special linear-gradient(90deg, #0b223e 50.02%, #00386f 100%);
  footer special linear-gradient(120.64deg, #0b223e 53.42%, #00386f 76.47%, #005eb8 100%);
  elev8-anywhere previous: #0363a8, #00253f, #0055a5
  */
  .sportsnet {
    :global([data-layout="weather-bottom"]) & {
      --layout-font-scale: 0.7;
    }
    :global(.text-container) {
      border-color: #e9ecef33;
    }
    :global(.title) {
      color: #fff;
    }
    :global(.title-container) {
      background-color: #002b49;
    }
    :global(.title-logo-container) {
      background-color: #005eb8;
    }
  }
  /* 
  citynews (scraped from website)
  white: #fff
  black: #0c0c0c
  primary: #c8102e (red)
  secondary: #770520 (dark red)
  neutral-dark: #363434
  neutral-light: #f2f2f2
  neutral: #e8e8e8
  */
  .citynews {
    :global([data-layout="weather-bottom"]) & {
      --layout-font-scale: 0.7;
    }
    :global(.text-container) {
      border-color: var(--vc-neutral-50);
    }
    :global(.title) {
      color: #0c0c0c;
      font-weight: 500;
    }
    :global(.title-container) {
      background-color: #fff;
      text-transform: uppercase;
    }
    :global(.title-logo-container) {
      /* flex: 1 1 3em; */
      padding: 0.3em;
      background-color: #0c0c0c;
      display: flex;
      /* max-width: 4em; */
    }
  }
  @keyframes slide-in-from-left {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes slide-in-from-left-text-content {
    0% {
      transform: translateX(-100%);
      opacity: 0;
      backdrop-filter: blur(0px);
      border-color: #ffffff00;
    }
    85% {
      backdrop-filter: blur(0);
      border-color: #ffffff00;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
      backdrop-filter: blur(1em);
    }
  }
  @keyframes slide-in-from-right {
    0% {
      transform: translateX(200%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
</style>
